import React, { useState, useEffect } from "react";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import * as api from "../lib/api";

export default function CommissionCalculator() {
  const [values, setValues] = useState({
    pListino: "", sF1: "", sF2: "", sF3: "",
    sCliente: "", pSegnalatore: "", pExtra: "",
  });
  const [archiSuppliers, setArchiSuppliers] = useState([]);
  const [archiQuery, setArchiQuery] = useState("");
  const [showArchiDrop, setShowArchiDrop] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.listArchiSuppliers().then(setArchiSuppliers).catch(() => {});
  }, []);

  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  const archiFiltered = (archiQuery.trim()
    ? archiSuppliers.filter((s) => norm(s.name).includes(norm(archiQuery)))
    : archiSuppliers
  ).slice(0, 8);

  const chainLabel = (s) => {
    const chain = Array.isArray(s.purchase_discount_chain) ? s.purchase_discount_chain : [];
    if (chain.length) return chain.join(" + ") + "%";
    if (s.purchase_discount_pct > 0) return `${s.purchase_discount_pct}%`;
    return null;
  };

  const selectArchiSupplier = (s) => {
    const chain = Array.isArray(s.purchase_discount_chain) ? s.purchase_discount_chain : [];
    const [d1, d2, d3] = chain;
    const fallbackPct = !chain.length && s.purchase_discount_pct > 0 ? s.purchase_discount_pct : null;
    setValues((prev) => ({
      ...prev,
      sF1: d1 != null ? String(d1) : fallbackPct != null ? String(fallbackPct) : "",
      sF2: d2 != null ? String(d2) : "",
      sF3: d3 != null ? String(d3) : "",
    }));
    setArchiQuery((s.name || "").trim());
    setShowArchiDrop(false);
    const label = chainLabel(s);
    if (label) toast.success(`Sconto acquisto caricato dai listini: ${label}`);
    else toast(`Nessuno sconto acquisto registrato sui listini per ${(s.name || "").trim()}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const formatCurrency = (val) =>
    isNaN(val) ? "€ 0,00" : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val);

  useEffect(() => {
    const pListino = parseFloat(values.pListino) || 0;
    if (pListino <= 0) { setResults(null); return; }
    const sF1 = parseFloat(values.sF1) || 0;
    const sF2 = parseFloat(values.sF2) || 0;
    const sF3 = parseFloat(values.sF3) || 0;
    const sCliente = parseFloat(values.sCliente) || 0;
    const pSegnalatore = parseFloat(values.pSegnalatore) || 0;
    const pExtra = parseFloat(values.pExtra) || 0;

    let costo = pListino * (1 - sF1 / 100);
    costo = costo * (1 - sF2 / 100);
    costo = costo * (1 - sF3 / 100);
    const venditaNetta = pListino * (1 - sCliente / 100);
    let prezzoFinale = pSegnalatore >= 100 ? 0 : venditaNetta / (1 - pSegnalatore / 100);
    const valProvvigione = prezzoFinale - venditaNetta;
    const profittoLordo = venditaNetta - costo;
    const valExtra = profittoLordo * (pExtra / 100);
    const profittoNetto = profittoLordo - valExtra;
    const margine = prezzoFinale > 0 ? (profittoNetto / prezzoFinale) * 100 : 0;

    setResults({ prezzoFinale, costo, venditaNetta, valProvvigione, valExtra, profittoNetto, margine });
  }, [values]);

  return (
    <div className="flex-1 bg-slate-50 p-6 overflow-y-auto custom-scrollbar" data-testid="calculator-view">
      <div className="w-full bg-white rounded-2xl border border-slate-200 p-6 md:p-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 tracking-tight">Calcolatore Provvigioni</h1>
          <p className="text-slate-500 mt-1 text-sm">Inserisci i dati per l&apos;analisi commerciale completa.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-2 uppercase tracking-wider">Prezzo di Listino (€)</label>
              <input
                type="number" name="pListino" value={values.pListino} onChange={handleChange}
                placeholder="es. 1000"
                className="w-full p-4 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-outfit font-semibold outline-none"
                data-testid="calc-pListino"
              />
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
              <h3 className="font-bold text-slate-500 text-[10px] uppercase tracking-wider mb-3">Dati Fornitore</h3>
              <div className="mb-4 relative">
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Fornitore ARCHI-HUB <span className="text-emerald-600 font-semibold">· sconti automatici dai listini</span>
                </label>
                <input
                  type="text"
                  value={archiQuery}
                  onChange={(e) => { setArchiQuery(e.target.value); setShowArchiDrop(true); }}
                  onFocus={() => setShowArchiDrop(true)}
                  onBlur={() => setTimeout(() => setShowArchiDrop(false), 150)}
                  placeholder="Cerca fornitore (es. Poliform, Flexform…)"
                  className="w-full p-3 bg-white border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-sm"
                  data-testid="archi-supplier-search"
                />
                {showArchiDrop && archiFiltered.length > 0 && (
                  <ul
                    className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    data-testid="archi-supplier-dropdown"
                  >
                    {archiFiltered.map((s) => {
                      const label = chainLabel(s);
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); selectArchiSupplier(s); }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-emerald-50 flex items-center justify-between gap-2"
                            data-testid={`archi-supplier-option-${s.id}`}
                          >
                            <span className="truncate text-slate-800">{(s.name || "").trim()}</span>
                            {label ? (
                              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded whitespace-nowrap">
                                {label}
                              </span>
                            ) : (
                              <span className="text-[11px] text-slate-400 whitespace-nowrap">nessuno sconto</span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">1° Sconto (%)</label>
                  <input type="number" name="sF1" value={values.sF1} onChange={handleChange} placeholder="es. 50" className="w-full p-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" data-testid="calc-sF1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">2° (%) <span className="text-slate-400">Opz.</span></label>
                    <input type="number" name="sF2" value={values.sF2} onChange={handleChange} placeholder="es. 5" className="w-full p-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" data-testid="calc-sF2" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">3° (%) <span className="text-slate-400">Opz.</span></label>
                    <input type="number" name="sF3" value={values.sF3} onChange={handleChange} placeholder="es. 2" className="w-full p-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" data-testid="calc-sF3" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
              <h3 className="font-bold text-slate-500 text-[10px] uppercase tracking-wider mb-3">Vendita & Extra</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Sconto Cliente (%)</label>
                  <input type="number" name="sCliente" value={values.sCliente} onChange={handleChange} placeholder="es. 10" className="w-full p-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" data-testid="calc-sCliente" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Provv. Segnalatore (%)</label>
                  <input type="number" name="pSegnalatore" value={values.pSegnalatore} onChange={handleChange} placeholder="es. 5" className="w-full p-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" data-testid="calc-pSegnalatore" />
                </div>
              </div>
            </div>

            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
              <label className="block text-xs font-medium text-amber-800 mb-1 uppercase tracking-wider">Percentuale Extra su Profitto (%)</label>
              <input type="number" name="pExtra" value={values.pExtra} onChange={handleChange} placeholder="es. 10" className="w-full p-3 bg-white border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none text-sm" data-testid="calc-pExtra" />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {results ? (
              <div className="space-y-5 h-full flex flex-col">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 rounded-2xl text-center shadow-lg">
                  <h3 className="font-semibold text-sm text-blue-100 mb-1 uppercase tracking-wider">Prezzo Finale al Cliente</h3>
                  <p className="text-4xl lg:text-5xl font-outfit font-extrabold tracking-tight" data-testid="calc-prezzoFinale">
                    {formatCurrency(results.prezzoFinale)}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-grow">
                  <ResultCard label="Mio Costo Acquisto" value={formatCurrency(results.costo)} color="red" testId="calc-costo" />
                  <ResultCard label="Vendita Netta" value={formatCurrency(results.venditaNetta)} color="orange" testId="calc-venditaNetta" />
                  <ResultCard label="Provv. Segnalatore" value={formatCurrency(results.valProvvigione)} color="purple" testId="calc-valProvvigione" />
                  <ResultCard label="Importo Extra" value={formatCurrency(results.valExtra)} color="teal" testId="calc-valExtra" />
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 col-span-1 sm:col-span-2 flex flex-col items-center text-center" data-testid="calc-profittoNetto">
                    <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">Tuo Profitto Netto</span>
                    <div className="flex items-baseline gap-3">
                      <span className="text-3xl font-outfit font-extrabold text-emerald-700">{formatCurrency(results.profittoNetto)}</span>
                      <span className="text-lg font-semibold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">{results.margine.toFixed(2)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 py-16">
                <Calculator size={64} className="text-slate-300 mb-4" strokeWidth={1.25} />
                <p className="text-lg font-outfit font-medium">Inizia a inserire i dati</p>
                <p className="text-sm">I risultati appariranno qui</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultCard({ label, value, color, testId }) {
  return (
    <div className={`bg-${color}-50 p-4 rounded-xl border border-${color}-200 flex flex-col justify-center`} data-testid={testId}>
      <span className={`text-[10px] font-medium text-${color}-600 uppercase tracking-wider`}>{label}</span>
      <span className={`block text-2xl font-outfit font-bold text-${color}-700 mt-1`}>{value}</span>
    </div>
  );
}
